import { useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';

import { tenantKeys } from './tenantKeys';

// Wire shape for GET /api/v1/tenants/me. Mirrors backend's TenantSettingsDto.
export const tenantSettingsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  brandingColor: z.string().nullable(),
});

export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

// GET the caller's tenant settings. Returns 404 for platform-only users
// (no tenant attachment); the OrganizationCard reads that as "hide me".
// retry: false (default for queries is 3 with backoff) -- we don't want
// the screen to wait 3 retries before showing the hidden state.
export function useGetCurrentTenant() {
  return useQuery({
    queryKey: tenantKeys.current(),
    queryFn: async ({ signal }) => {
      try {
        const raw = await apiClient.get('api/v1/tenants/me', { signal }).json();
        return tenantSettingsSchema.parse(raw);
      } catch (error) {
        // 404 is an expected outcome (platform user / no tenant). Map it
        // to a null result so the consumer can render conditionally
        // without a try/catch in the component.
        if (error instanceof HTTPError && error.response.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}
